import React from 'react';
import Popup from 'components/Popup';
import ComboBox from 'components/Common/ComboBox';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
	setCallReceptionStatus,
	subscribeToCalls,
	setAuthorizationSuccess,
	getOperatorStatus,
} from 'actions/call';
import PropTypes from 'prop-types';
import styles from 'styles/modules/userBox.module.scss';
import { translate } from 'react-i18next';
import URLS from 'config/urls';
import { sessionStorageService } from 'services/settingsService';
import UserCallForm from './UserCallForm';
import ClickOutsideHolder from 'components/ClickOutsideHolder';
import permissions, { checkPermissions } from 'config/permissions';
import store from 'store';
import * as api from 'api';

const getUserPopupOperatorActionsCount = (operatorActions = []) => {
	if (Array.isArray(operatorActions)) {
		return operatorActions.length;
	}

	const numericCount = Number(operatorActions);

	return Number.isFinite(numericCount) ? numericCount : 0;
};

const shouldRequestUserPopupStatus = (login, operatorActions = []) =>
	!!login && getUserPopupOperatorActionsCount(operatorActions) === 0;

const normalizeUserPopupOperatorActions = (operatorActions = []) =>
	(Array.isArray(operatorActions) ? operatorActions : []);

const normalizeUserPopupRoleValue = (value) => String(value || '')
	.trim()
	.toLowerCase()
	.replace(/[\s_-]+/g, '');

const collectUserPopupRoleTokens = (user = {}) => {
	const roleSources = [];

	if (user.user_role) {
		roleSources.push(user.user_role);
	}

	if (Array.isArray(user.groups)) {
		roleSources.push(...user.groups);
	}

	if (Array.isArray(user.roles)) {
		roleSources.push(...user.roles);
	} else if (user.roles && typeof user.roles === 'object') {
		roleSources.push(...Object.keys(user.roles), ...Object.values(user.roles));
	}

	return new Set(
		roleSources
			.filter((item) => typeof item === 'string' || typeof item === 'number')
			.map(normalizeUserPopupRoleValue)
			.filter(Boolean),
	);
};

const shouldShowPersonalCabinet = (user = {}) => {
	const {
		isOperator,
	} = getUserPopupCabinetRoleState(user);

	return isOperator;
};

const shouldShowOvvCabinetLinkHidden = (user = {}) => {
	const {
		isExecutor,
	} = getUserPopupCabinetRoleState(user);

	return isExecutor;
};

const getUserPopupCabinetRoleState = (user = {}) => {
	const roles = collectUserPopupRoleTokens(user);
	const isAdmin = Boolean(user.admin);
	const isSuperAdmin = roles.has('superadmin') || isAdmin;
	const isOperator = isSuperAdmin
		|| roles.has('operatorukc')
		|| roles.has('operatordomain')
		|| roles.has('domainoperator');
	const isExecutor = isSuperAdmin || roles.has('executorovv');
	const hasDualCabinets = isSuperAdmin || (isOperator && isExecutor);

	return {
		isSuperAdmin,
		isOperator,
		isExecutor,
		hasDualCabinets,
	};
};

const getUserPopupCabinetLinkLabels = (user = {}) => {
	const { hasDualCabinets } = getUserPopupCabinetRoleState(user);

	if (hasDualCabinets) {
		return {
			operator: 'Кабінет оператора',
			executor: 'Кабінет виконавця',
		};
	}

	return {
		operator: 'Особистий кабінет',
		executor: 'Особистий кабінет',
	};
};

const mapStateToProps = (state) => ({
	phoneNumber: state.call.phoneNumber,
	callReceptionStatus: state.call.callReceptionStatus,
	isAuthorized: state.call.isAuthorized,
	operatorActions: state.call.operatorActions,
	callStation: state.call.callStation,
	user: state.user,
	shouldReadStatus: checkPermissions(permissions.CallOperations.action_workPbx),
});

function mapDispatchToProps(dispatch) {
	return bindActionCreators(
		{
			setCallReceptionStatus,
			subscribeToCalls,
			setAuthorizationSuccess,
			getOperatorStatus,
		},
		dispatch,
	);
}

@translate()
@connect(mapStateToProps, mapDispatchToProps)
class UserPopup extends React.Component {
	constructor(props) {
		super(props);
		this.avatarCacheKey = Date.now();
		this.authorizing = false;
		this.onStatusChange = this.onStatusChange.bind(this);
	}

	componentDidMount() {
		const isOpenedBefore = sessionStorageService.get('isUserPopupOpenedBefore');

		if (!isOpenedBefore) {
			sessionStorageService.set('isUserPopupOpenedBefore', true);
		}

		const { operatorActions, user } = this.props;

		if (shouldRequestUserPopupStatus(user && user.login, operatorActions)) {
			this.props.getOperatorStatus(this.props.user.login);
		}
	}

	componentDidUpdate(prevProps) {
		const { operatorActions, user } = this.props;

		if (shouldRequestUserPopupStatus(user && user.login, operatorActions) && !(prevProps.user && prevProps.user.login)) {
			this.props.getOperatorStatus(this.props.user.login);
		}
	}

	componentWillUnmount() {
		if (this.isActiveCallReceptionsStatus() && !this.props.isAuthorized) {
			this.props.setCallReceptionStatus('inactive');
		}
	}

	logOut = () => {
		localStorage.removeItem('ciscoLogin');
		localStorage.removeItem('ciscoPassword');
		sessionStorage.clear();
		window.location.href = URLS.core.logout.url;
	};

	isActiveCallReceptionsStatus = () =>
		this.props.callReceptionStatus === 'returntowork';

	getPhotoSource = () => {
		const { user_photo } = this.props.user;

		if (!user_photo) {
			return '';
		}

		const normalized = String(user_photo).trim();
		const isJsonPayload = normalized.startsWith('{') || normalized.startsWith('[');

		if (normalized.startsWith('data:image')) {
			if (normalized.includes('{') || isJsonPayload) {
				return `/mw/file/avatar?v=${this.avatarCacheKey}`;
			}
			return normalized;
		}

		if (isJsonPayload) {
			return `/mw/file/avatar?v=${this.avatarCacheKey}`;
		}

		if (normalized.startsWith('http') || normalized.startsWith('/') || normalized.startsWith('mw/')) {
			return normalized;
		}

		return `data:image/png;base64,${normalized}`;
	};

	getPopupAvatar = () =>
		this.props.user.user_photo ? (
			<div className={styles.popupAvatarWrapper}>
				<img alt=""
					className="popup-user-image"
					src={this.getPhotoSource()}
				/>
			</div>
		) : (
			<div className="popup-circle-wrapper">
				<div className="icon-user-wrapper">
					<i className="icon-user" />
				</div>
			</div>
		);

	onStatusChange({ value }) {
		const { user, setCallReceptionStatus } = this.props;
		setCallReceptionStatus(value, user.login);
	}

	handleClickOutside = (event) => {
		if (!this.authorizing) {
			this.props.handleClickOutside(event);
		}
	};

	goToPersonalCabinet = () => {
		window.location.hash = `#/personal-cabinet`;
		this.props.onClose();
	};

	goToOvvCabinet = () => {
		window.location.hash = '#/ovv-cabinet';
		this.props.onClose();
	};

	onCallFormSubmit = ({ phoneNumber, agentName, agentPassword }) => {
		const { callStation } = this.props;
		this.authorizing = true;

		const params = {
			phone: phoneNumber,
			login: agentName,
			authType: 'Basic',
			authValue: agentPassword,
		};

		return store
			.dispatch(api.sn.callEvent.auth.initiate(params))
			.unwrap()
			.then((response) => {
				if (response.success && response.result.confirmed) {
					this.authorizing = false;

					store.dispatch(
						api.mw.operator.operatorSet.initiate({ phone: phoneNumber }),
					);

					this.props.setAuthorizationSuccess(phoneNumber, this.props.user.login);
					this.props.subscribeToCalls();
					this.props.onClose();
				} else {
					throw new Error('SSE authentication error');
				}
			})
			.catch((e) => {
				console.log(e);
				this.authorizing = false;
				return callStation === 'asterisk'
					? 'Некоректний номер телефону'
					: 'Некоректний логін або пароль';
			});
	};

	render() {
		const {
			user,
			callReceptionStatus,
			formRef,
			t,
			operatorActions,
			shouldReadStatus,
		} = this.props;
		const normalizedOperatorActions = normalizeUserPopupOperatorActions(operatorActions);
		const canViewPersonalCabinet = shouldShowPersonalCabinet(user);
		const shouldRenderHiddenOvvCabinetLink = shouldShowOvvCabinetLinkHidden(user);
		const cabinetLinkLabels = getUserPopupCabinetLinkLabels(user);

		return (
			<ClickOutsideHolder onClickOutside={this.handleClickOutside}>
				<Popup place="user-info-popup">
					<div className="popup-main">
						<div className="popup-wrapper">
							{this.getPopupAvatar()}

							<div className="popup-text-wrapper">
								<div className="popup-name">{user.login}</div>
								<div className="popup-on-line" />
								{canViewPersonalCabinet ? (
									<button
										className="popup-user-page"
										type="button"
										onClick={this.goToPersonalCabinet}
									>
										{cabinetLinkLabels.operator}
									</button>
								) : null}
								{shouldRenderHiddenOvvCabinetLink ? (
									<button
										className="popup-user-page"
										type="button"
										onClick={this.goToOvvCabinet}
									>
										{cabinetLinkLabels.executor}
									</button>
								) : null}
							</div>
						</div>

						<div className="popup-select" ref={formRef}>
							{shouldReadStatus ? (
								<div className="row">
									<div className="input-element">
										<div className="input-label">{t('status_name')}</div>

										<ComboBox
											name="status"
											placeholder={t('call.chooseStatus')}
											options={normalizedOperatorActions}
											value={callReceptionStatus}
											devaultValue={callReceptionStatus}
											onChange={this.onStatusChange}
										/>
									</div>
								</div>
							) : null}

							{this.isActiveCallReceptionsStatus() && (
								<UserCallForm user={user} onSubmit={this.onCallFormSubmit} />
							)}
						</div>
					</div>

					<div className="popup-footer exit">
						<button type="button"
							className="menu-item"
							onClick={this.logOut}
						>
							<i className="icon-sign-out" />
							<span className="menu-item-text">{t('userExit')}</span>
						</button>
					</div>
				</Popup>
			</ClickOutsideHolder>
		);
	}
}

UserPopup.propTypes = {
	handleClickOutside: PropTypes.func,
	user: PropTypes.object,
	formRef: PropTypes.object,
	onClose: PropTypes.func,
};

UserPopup.defaultProps = {
	operatorActions: [],
	user: {},
};

export default UserPopup;
